import moment from 'moment';
import eventbus from './eventbus';

import appSettings from './appSettings';
import bedBoardUtils from './bedBoardUtils';

// options = {
//   storageScheme: null, // 'local', 'session'
// systemID: null // 'BedBaord'
// }

function install(Vue, options) {
  if (options.storageKeyPrefix && options.systemID) {
    Vue.prototype.$sdkAuth = {
      storageKeyPrefix: options.storageKeyPrefix,
      storageKeyForLoginParameters: `${options.storageKeyPrefix}loginParameters`,
      storageKeyForRememberMe: options.storageKeyForRememberMe ? `${options.storageKeyPrefix}${options.storageKeyForRememberMe}` : `${options.storageKeyPrefix}rememberMe`,
      storageKeyForScope: options.storageKeyForScope ? `${options.storageKeyPrefix}${options.storageKeyForScope}` : `${options.storageKeyPrefix}scope`,
      storageKeyForUserInfo: options.storageKeyForUserInfo ? `${options.storageKeyPrefix}${options.storageKeyForUserInfo}` : `${options.storageKeyPrefix}userInfo`,
      storageKeyForUserName: options.storageKeyForUserName ? `${options.storageKeyPrefix}${options.storageKeyForUserName}` : `${options.storageKeyPrefix}userName`,
      systemID: options.systemID,

      getLoginParameters(storageScheme) {
        return JSON.parse(this.getStorageItem(this.storageKeyForLoginParameters, storageScheme));
      },

      getRememberMe(storageScheme) {
        let returnValue = false;
        if (this.storageKeyPrefix && this.storageKeyForRememberMe) {
          const storageValue = this.getStorageItem(this.storageKeyForRememberMe, storageScheme);
          returnValue = storageValue;
        }
        return returnValue;
      },

      getScope(storageScheme) {
        return this.getStorageItem(this.storageKeyForScope, storageScheme);
      },

      getStorageItem(key, storageScheme) {
        let returnValue = null;
        if (/\S/.test(key)) {
          if (storageScheme === 'local') {
            returnValue = localStorage.getItem(key);
          } else if (storageScheme === 'session') {
            returnValue = sessionStorage.getItem(key);
          }
        }
        return returnValue;
      },

      getUserInfo(storageScheme) {
        return JSON.parse(this.getStorageItem(this.storageKeyForUserInfo, storageScheme));
      },

      getUserName(storageScheme) {
        return this.getStorageItem(this.storageKeyForUserName, storageScheme);
      },

      isUserAuthenticated() {
        let returnValue = false;

        const loginParameters = this.getLoginParameters('session');
        if (loginParameters) {
          const { ntstOAuthTokenData } = loginParameters;
          if (ntstOAuthTokenData && ntstOAuthTokenData.accessTokenCreatedDate) {
            const momentAccessTokenCreatedDate = moment.utc(new Date(ntstOAuthTokenData.accessTokenCreatedDate.valueOf()));
            if (momentAccessTokenCreatedDate.isValid()) {
              const expirationDate = moment.utc(new Date(momentAccessTokenCreatedDate.valueOf())).add(ntstOAuthTokenData.ntstOAuthExpiresIn, 'seconds');
              const now = moment.utc();
              if (now <= expirationDate) {
                returnValue = true;
              }
            }
          }
        }
        return returnValue;
      },

      login(loginParameters) {
        return new Promise(async (resolve, reject) => {
          if (loginParameters) {
            loginParameters.systemID = this.systemID;
            if (loginParameters.rememberMe) {
              this.setStorageItem(this.storageKeyForRememberMe, loginParameters.rememberMe, 'local');
            } else {
              this.removeStorageItem(this.storageKeyPrefix + this.storageKeyForRememberMe, 'local');
            }

            try {
              if (loginParameters && loginParameters.authorizationType === 'SsoOa') {
                if (this.setUserAuthInfo(loginParameters)) {
                  resolve();
                } else {
                  reject(new Error('User/Auth Info not valid. (SsoOa)'));
                }
              } else {
                bedBoardUtils.executeLogin(loginParameters)
                  .then((data) => {
                    if (data && data.value && data.value.verifiedStaffMember) {
                      loginParameters.ntstOAuthTokenData = bedBoardUtils.getNtstOAuthTokenDataFromVerifySystemAccountOutputPayload(data.value);
                      loginParameters.providerID = bedBoardUtils.getStaffMemberProviderIDForStaffMember(data.value.verifiedStaffMember);
                      loginParameters.providerName = bedBoardUtils.getProviderNameForStaffMember(data.value.verifiedStaffMember);

                      if (this.setUserAuthInfo(loginParameters)) {
                        resolve(data.value);
                      } else {
                        reject(new Error('User/Auth Info not valid.'));
                      }
                    } else if (data) {
                      reject(new Error('No value or verified staff member received.'));
                    } else {
                      reject(new Error('Invalid result from login.'));
                    }
                  }).catch((err) => {
                    reject(err);
                  });
              }
            } catch (err) {
              reject(err);
            }
          } else {
            reject(new Error('no login params'));
          }
        });
      },

      logout(message) {
        this.removeStorageItem(this.storageKeyForUserInfo, 'session');
        this.removeStorageItem(this.storageKeyForLoginParameters, 'session');
        eventbus.$emit('LogoutSuccessful', message);
      },

      logoutSSO() {
        this.removeStorageItem(this.storageKeyForUserInfo, 'session');
        this.removeStorageItem(this.storageKeyForLoginParameters, 'session');
      },

      removeStorageItem(key, storageScheme) {
        if (/\S/.test(key)) {
          if (storageScheme === 'local') {
            localStorage.removeItem(key);
          } else if (storageScheme === 'session') {
            sessionStorage.removeItem(key);
          }
        }
      },

      setStorageItem(key, value, storageScheme) {
        if (/\S/.test(key)) {
          if (storageScheme === 'local') {
            localStorage.setItem(key, value);
          } else if (storageScheme === 'session') {
            sessionStorage.setItem(key, value);
          }
        }
      },

      setUserAuthInfo(loginParameters) {
        let validUserInfo = true;

        if (loginParameters && loginParameters.ntstOAuthTokenData && loginParameters.providerID) {
          this.setStorageItem(this.storageKeyForScope, loginParameters.scope, 'local');
          this.setStorageItem(this.storageKeyForScope, loginParameters.scope, 'session');
          if (loginParameters.rememberMe) {
            this.setStorageItem(this.storageKeyForUserName, loginParameters.userName, 'local');
          } else if (!loginParameters.ntstOAuthTokenData) {
            this.removeStorageItem(this.storageKeyForUserName, 'local');
          }
          const userInfo = {
            name: loginParameters.providerName,
            providerID: loginParameters.providerID,
            userName: loginParameters.userName
          };
          this.setStorageItem(this.storageKeyForUserInfo, JSON.stringify(userInfo), 'session');
          this.setStorageItem(this.storageKeyForLoginParameters, JSON.stringify(loginParameters), 'session');
          if (loginParameters.isLogoutDisabled) {
            eventbus.$emit('SetIsLogoutDisabled', loginParameters.isLogoutDisabled);
          }

          if (loginParameters.ntstOAuthTokenData.ntstOAuthExpiresIn) {
            this.tokenExpiresAfterMinutes = parseInt(loginParameters.ntstOAuthTokenData.ntstOAuthExpiresIn, 10);
          }
        } else {
          validUserInfo = false;
        }
        return validUserInfo;
      },

      updateAuthIfNeeded() {
        return new Promise(async (resolve, reject) => {
          Vue.$log.debug(`[${moment().format(appSettings.logDateFormat)}] sdkAuth.updateAuthIfNeeded: Checking if Auth needs to be updated.`);

          const loginParameters = this.getLoginParameters('session');
          if (loginParameters) {
            const { ntstOAuthTokenData } = loginParameters;

            if (ntstOAuthTokenData && ntstOAuthTokenData.accessTokenCreatedDate) {
              const momentAccessTokenCreatedDate = moment.utc(new Date(ntstOAuthTokenData.accessTokenCreatedDate.valueOf()));

              if (momentAccessTokenCreatedDate.isValid()) {
                const expirationDate = moment.utc(new Date(momentAccessTokenCreatedDate.valueOf())).add(ntstOAuthTokenData.ntstOAuthExpiresIn, 'seconds');
                const now = moment.utc();
                const adjustedExpirationDateForRefresh = moment.utc(new Date(expirationDate.valueOf())).subtract(appSettings.numberOfMinutesBeforeTokenExpiresToRefresh, 'minutes');
                if (now > adjustedExpirationDateForRefresh) {
                  Vue.$log.debug(`[${moment().format(appSettings.logDateFormat)}] sdkAuth.updateAuthIfNeeded: AccessToken is about to expire in ${expirationDate.diff(now, 'minutes', true)} minutes, attempting to refresh. `);
                  Vue.$log.debug(`[${moment().format(appSettings.logDateFormat)}] sdkAuth.updateAuthIfNeeded: TokenExpireTime: ${expirationDate.format(appSettings.logDateFormat)}, AdjustedExpirationDateForRefresh: ${adjustedExpirationDateForRefresh.format(appSettings.logDateFormat)} `);
                  bedBoardUtils.executeRefreshToken()
                    .then((returnedLoginParameters) => {
                      if (returnedLoginParameters.ntstOAuthTokenData && returnedLoginParameters.ntstOAuthTokenData.accessTokenCreatedDate) {
                        this.updateNtstOAuthTokenData(returnedLoginParameters.ntstOAuthTokenData, 'session');
                        Vue.$log.debug(`[${moment().format(appSettings.logDateFormat)}] updateAuthIfNeeded: Refresh token returned all of the required token data. `);
                        resolve(true);
                      } else if (now > expirationDate) {
                        reject(new Error('sdkAuth.updateAuthIfNeeded: Refresh token didn\'t return all of the required token data.'));
                      } else {
                        reject(new Error('sdkAuth.updateAuthIfNeeded: Refresh token didn\'t return all of the required token data.'));
                      }
                    })
                    .catch((err) => {
                      this.errorMessage = err.message;
                      this.isBusy = false;
                      const rejectError = new Error(`sdkAuth.updateAuthIfNeeded: Error updating token: '${err.message}' `);
                      if (now > expirationDate) {
                        rejectError.logUserOut = true;
                      }
                      reject(rejectError);
                    });
                } else {
                  Vue.$log.debug(`[${moment().format(appSettings.logDateFormat)}] sdkAuth.updateAuthIfNeeded: AccessToken has not expired, auth is still valid for ${expirationDate.diff(now, 'minutes', true)} minutes. `);
                  resolve(false);
                }
              } else {
                reject(new Error(`sdkAuth.updateAuthIfNeeded: Moment AccessTokenCreatedDate is not valid: '${momentAccessTokenCreatedDate}' `));
              }
            } else {
              reject(new Error('sdkAuth.updateAuthIfNeeded: No OAuthTokenData or AccessTokenCreatedDate value found in session LoginParameters.'));
            }
          } else {
            reject(new Error('sdkAuth.updateAuthIfNeeded: No login parameters found in session.'));
          }
        });
      },

      updateNtstOAuthTokenData(ntstOAuthTokenData, storageScheme) {
        if (ntstOAuthTokenData) {
          const loginParameters = this.getLoginParameters(storageScheme);
          if (loginParameters && loginParameters.ntstOAuthTokenData) {
            loginParameters.ntstOAuthTokenData = ntstOAuthTokenData;
            this.setStorageItem(this.storageKeyForLoginParameters, JSON.stringify(loginParameters), 'session');
          }
        }
      }
    };
  } else {
    this.$log.error('All options not provided for SDK Authentication.');
  }
}

export default {
  install
};
